import { useEffect, useRef, useState } from "react";
import { CheckboxInput, RadioList } from "swing-components";
import { translate } from "utils/string-table";

import {
  ModalSubFeedbackBody,
  ModalSubFeedbackFooter,
  ModalSubFeedbackHeader,
  type NegativeData,
  type NegativeFeedbackReasons,
  type NegativeStep,
} from "~components/ModalSubFeedback";

/********* Types ********/
type UpdatedNegativeReasons = Pick<NegativeData, "reasons" | "otherReason">;

type ModalSubFeedbackContentNegativeReasonsProps = {
  negativeData: NegativeData;
  onDismiss: () => void;
  setNegativeData: (data: NegativeData) => void;
  setNegativeStep: (step: NegativeStep) => void;
};

/********* Component ********/
export function ModalSubFeedbackContentNegativeReasons(
  props: ModalSubFeedbackContentNegativeReasonsProps,
) {
  const { negativeData, onDismiss, setNegativeData, setNegativeStep } = props;

  /********* Hooks ********/
  const [_updatedReasons, _setUpdatedReasons] = useState<UpdatedNegativeReasons>({
    reasons: negativeData.reasons,
    otherReason: negativeData.otherReason ?? "",
  });

  const previousReasonsRef = useRef<NegativeFeedbackReasons[]>(negativeData.reasons);

  useEffect(() => {
    _setUpdatedReasons((prev) => {
      // If switching to blocking a sub, only keep the most recent reason
      if (negativeData.isBlocked) {
        return {
          ...prev,
          reasons:
            previousReasonsRef.current.length > 0
              ? [previousReasonsRef.current[previousReasonsRef.current.length - 1]]
              : [],
        };
      }

      // If switching back to non-blocking a sub, restore previous selections
      return {
        ...prev,
        reasons:
          prev.reasons.length === 1 && previousReasonsRef.current.length > 0
            ? previousReasonsRef.current
            : prev.reasons,
      };
    });
  }, [negativeData.isBlocked]);

  /********* Handlers ********/
  function handleSelectionChange(reason: NegativeFeedbackReasons) {
    _setUpdatedReasons((prev) => {
      if (negativeData.isBlocked) {
        // Store previous reasons before switching to blocking a sub
        previousReasonsRef.current = prev.reasons;

        return {
          reasons: [reason], // Blocked sub/radio button mode: Only one reason allowed
          otherReason: reason === "REASON_OTHER" ? prev.otherReason : undefined,
        };
      } else {
        // Restore previous selections when switching back
        const updatedReasons = prev.reasons.includes(reason)
          ? prev.reasons.filter((r) => r !== reason)
          : [...prev.reasons, reason];

        return {
          reasons: updatedReasons,
          otherReason: updatedReasons.includes("REASON_OTHER") ? prev.otherReason : undefined,
        };
      }
    });
  }

  function handleOtherReason(reason: string) {
    _setUpdatedReasons((prev) => ({ ...prev, otherReason: reason }));
  }

  function handleOnNext() {
    const updatedData = {
      ...negativeData,
      ..._updatedReasons,
      // Don't add otherReason field if it's empty
      ...(_updatedReasons.otherReason?.trim() && { otherReason: _updatedReasons.otherReason }),
    };

    setNegativeData(updatedData);
    setNegativeStep("comments");
  }

  /********* Constants ********/
  const selectedReasons = Object.keys(NegativeReasonsSelections) as NegativeFeedbackReasons[];
  const isNextDisabled =
    !_updatedReasons.reasons?.length ||
    (_updatedReasons.reasons.includes("REASON_OTHER") &&
      !negativeData.isBlocked &&
      !_updatedReasons.otherReason?.trim());

  /********* Render ********/
  return (
    <>
      <ModalSubFeedbackHeader title={translate("GIVE_FEEDBACK_LABEL")} onDismiss={onDismiss} />
      <ModalSubFeedbackBody>
        <p css={{ fontWeight: 600 }}>{translate("MSF_NEGATIVE_FEEDBACK_REASON_LABEL")}</p>
        {!negativeData.isBlocked && <p>{translate("LABEL_SELECT_ALL_THAT_APPLY")}</p>}
        <div css={{ marginTop: 16 }}>
          {negativeData.isBlocked ? (
            <RadioList
              groupName="negative-sub-feedback"
              value={_updatedReasons.reasons[0]}
              options={selectedReasons.map((reason) => ({
                value: reason,
                label: NegativeReasonsSelections[reason],
                textField:
                  reason === "REASON_OTHER"
                    ? {
                        isRequired: true,
                        label: translate("MSF_REASON_OTHER_TEXT_INPUT_LABEL"),
                        value: _updatedReasons.otherReason,
                        onChange: (reason: string) => handleOtherReason(reason),
                        placeholder: translate(
                          "MSF_NEUTRAL_NEGATIVE_REASON_OTHER_TEXT_INPUT_PLACEHOLDER",
                        ),
                      }
                    : undefined,
              }))}
              onChange={(value) => value && handleSelectionChange(value as NegativeFeedbackReasons)}
            />
          ) : (
            selectedReasons.map((reason) => {
              return (
                <CheckboxInput
                  key={reason}
                  label={NegativeReasonsSelections[reason]}
                  isChecked={_updatedReasons.reasons.includes(reason)}
                  onCheckedChange={() => handleSelectionChange(reason)}
                  hasBorderBottom={reason !== "REASON_OTHER"}
                  hasInputField={reason === "REASON_OTHER"}
                  textAreaProps={{
                    isRequired: true,
                    label: translate("MSF_REASON_OTHER_TEXT_INPUT_LABEL"),
                    value: _updatedReasons.otherReason,
                    onChange: (reason: string) => handleOtherReason(reason),
                    placeholder: translate(
                      "MSF_NEUTRAL_NEGATIVE_REASON_OTHER_TEXT_INPUT_PLACEHOLDER",
                    ),
                  }}
                />
              );
            })
          )}
        </div>
      </ModalSubFeedbackBody>
      <ModalSubFeedbackFooter
        buttonPrimary={{
          onClick: handleOnNext,
          isDisabled: isNextDisabled,
          text: translate("LABEL_NEXT"),
        }}
        buttonSecondary={{
          onClick: () => setNegativeStep("block"),
          isDisabled: false,
          text: translate("LABEL_BACK"),
        }}
      />
    </>
  );
}

// Note: Order matters here - it controls the order the UI displays the reasons
export const NegativeReasonsSelections: Record<NegativeFeedbackReasons, string> = {
  REASON_PHONE: translate("MSF_NEGATIVE_FEEDBACK_REASON_PHONE"),
  REASON_DISENGAGED: translate("MSF_NEGATIVE_FEEDBACK_REASON_DISENGAGED"),
  REASON_DIRECTIONS: translate("MSF_NEGATIVE_FEEDBACK_REASON_DIRECTIONS"),
  REASON_RELIABILITY: translate("MSF_NEGATIVE_FEEDBACK_REASON_RELIABILITY"),
  REASON_MANAGEMENT: translate("MSF_NEGATIVE_FEEDBACK_REASON_MANAGEMENT"),
  REASON_SAFETY: translate("MSF_NEGATIVE_FEEDBACK_REASON_SAFETY"),
  REASON_ATTITUDE: translate("MSF_NEGATIVE_FEEDBACK_REASON_ATTITUDE"),
  REASON_OTHER: translate("MSF_REASON_OTHER"),
};
